import { mergeAndDedupeOptions } from './merge-and-dedupe-options';

/**
 * Fetches filter options for each selected site, falling back to the current cloudId when no sites
 * are selected. It tolerates an individual site failure, then merges and deduplicates the
 * successful results. A single site uses the original direct request path.
 */
export const fetchOptionsAcrossSites = async <T extends { value?: string; trackingKey?: string }>(
	config: Readonly<{
		cloudId?: string;
		selectedCloudIds?: readonly string[];
	}>,
	fetchOptionsForSite: (cloudId: string) => Promise<T[]>,
): Promise<T[]> => {
	// TODO: Use selectedCloudIds exclusively once the multi-site search experiment is cleaned up.
	const cloudIds = config.selectedCloudIds?.length
		? config.selectedCloudIds
		: [config.cloudId || ''];

	if (cloudIds.length === 1) {
		return fetchOptionsForSite(cloudIds[0]);
	}

	const settledPerSite = await Promise.allSettled(
		cloudIds.map((cloudId) => fetchOptionsForSite(cloudId)),
	);
	const succeeded = settledPerSite.filter(
		(result): result is PromiseFulfilledResult<T[]> => result.status === 'fulfilled',
	);

	// One unhealthy site should not blank out the whole filter; only fail if every site failed.
	if (succeeded.length === 0) {
		throw (settledPerSite[0] as PromiseRejectedResult).reason;
	}

	return mergeAndDedupeOptions(succeeded.map((result) => result.value));
};
